import { useState } from "react"
import { Search } from "lucide-react"

import { ScenarioNotice } from "@/components/ScenarioNotice"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { KepKeyReader } from "@/components/kep/KepKeyReader"

/**
 * document-editor.md's DE4 — `EdrtzDocumentRequestDialog`, opened from the
 * ТЗ tab's "Запит до ЄДРТЗ (Необхідний КЕП)" action. The request form
 * (plate/VIN, prefilled from the draft's current values on every open) and
 * the КЕП step (the real, already-wired `KepKeyReader`) are both real. The
 * documented next step — a mock "Результат запиту до ЄДРТЗ" dialog that
 * lets the operator overwrite mapped ТЗ/owner fields — needs an actual
 * ЄДРТЗ response and is explicitly out of scope for this pass (no backend
 * call, no fabricated response data): reading a key here surfaces an honest
 * "not wired yet" notice instead of inventing a result.
 */
export function EdrtzRequestDialog({
  plate,
  vin,
}: {
  plate: string
  vin: string
}) {
  const [open, setOpen] = useState(false)
  const [plateQuery, setPlateQuery] = useState(plate)
  const [vinQuery, setVinQuery] = useState(vin)
  const [requested, setRequested] = useState(false)

  return (
    <Dialog
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          setPlateQuery(plate)
          setVinQuery(vin)
          setRequested(false)
        }
      }}
      open={open}
    >
      <DialogTrigger asChild>
        <Button length="full-size">
          <Search data-icon="inline-start" />
          Запит до ЄДРТЗ (Необхідний КЕП)
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Запит до ЄДРТЗ</DialogTitle>
          <DialogDescription>
            Запит до ЄДРТЗ безкоштовний. Перед відправленням його підписує
            КЕП відповідальної особи.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel htmlFor="edrtz-plate">Номерний знак</FieldLabel>
            <Input
              id="edrtz-plate"
              onChange={(event) => setPlateQuery(event.target.value)}
              placeholder="АА0000BB"
              value={plateQuery}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="edrtz-vin">VIN-код</FieldLabel>
            <Input
              id="edrtz-vin"
              onChange={(event) => setVinQuery(event.target.value)}
              placeholder="17 символів"
              value={vinQuery}
            />
          </Field>
          {requested ? (
            <ScenarioNotice tone="default">
              Інтеграцію із ЄДРТЗ ще не підключено в цій версії — ключ
              зчитано, але запит на сервер не надсилається.
            </ScenarioNotice>
          ) : (
            <KepKeyReader
              actionLabel="Підписати КЕП і виконати запит"
              onRead={() => setRequested(true)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
